import { DetailsPageTopControlsView } from "../common/panels/misc/DetailsPageTopControlsView/DetailsPageTopControlsView";
import { NotSet } from "../common/misc/UIconstants";
import Detail from "../common/panels/Detail/Detail";
import { Table } from "../common/panels/Detail/DetailTable";
import { EntityDetailProps, EntityDetailTableProps } from "../common/entity/EntityInterfaces";
import { toUppercase } from "../common/helperfunctions/stringFunctions";
import { customImportConstants, CustomImportFilters } from "../api/CustomImports";
import { Button } from "../common/buttons/Button/Button";
import { LucideIcon } from "../common/icon/LucideIcon";
import { usePostMutation } from "../api/BaseEntityApi";
import { useQueryClient } from "@tanstack/react-query";
import { useEntityPermissions } from "../common/permissions/useEntityPermissions";

export const CustomImportsDetailTable = ({
  entity,
  entityConstants,
  fieldLabels,
  showLinks = true,
}: EntityDetailTableProps<"customImports">) => {
  return (
    <>
      <Table noPadding nonFoldable>
        <Table.Head>Details</Table.Head>
        <Table.Body>
          <Table.Body.RowContent
            title={fieldLabels.name}
            content={
              <div className="container_label">
                <div className="container_label_name" style={{ fontWeight: 600 }}>
                  {entity.name ?? NotSet}
                </div>
                <div className="container_label_id">
                  <span style={{ whiteSpace: "nowrap" }}>
                    {toUppercase(entityConstants.entitySingular)}-ID: {entity.id}
                  </span>
                </div>
              </div>
            }
          />

          <Table.Body.RowContent
            title={fieldLabels.variables}
            content={
              <pre style={{ width: "100%", padding: "10px", margin: 0 }}>
                {JSON.stringify(entity.variables, null, 2)}
              </pre>
            }
          />

          <Table.Body.RowContent
            title={fieldLabels.variableMappings}
            content={
              <pre style={{ width: "100%", padding: "10px", margin: 0 }}>
                {JSON.stringify(entity.variableMappings, null, 2)}
              </pre>
            }
          />

          <Table.Body.RowContent
            title={fieldLabels.rules}
            content={
              <pre style={{ width: "100%", padding: "10px", margin: 0 }}>{JSON.stringify(entity.rules, null, 2)}</pre>
            }
          />
        </Table.Body>
      </Table>
    </>
  );
};

export const CustomImportsDetail = ({
  permissions,
  routes,
  entity,
  entityApi,
  entityConstants,
  fieldLabels,
  entityTypeId,
}: EntityDetailProps<"customImports">) => {
  return (
    <Detail
      head={
        <DetailsPageTopControlsView
          entityTypeId={entityTypeId}
          entity={entity}
          entityConstants={customImportConstants}
          hideHistoryModal
          controls={
            <div className="flex align-center gap-5">
              <ReloadMetadataTemplateConfigurationsButton filters={{ ids: [entity.id] }} />
            </div>
          }
        />
      }
      mainStyle={{ padding: "0px 10px" }}
      main={
        <CustomImportsDetailTable
          entity={entity}
          entityConstants={entityConstants}
          fieldLabels={fieldLabels}
          entityTypeId={entityTypeId}
        />
      }
      sidebarPosition="hidden"
      // sidebar={<></>}
    />
  );
};

// This button is used to reload the metadata template configurations from the configuration files.
// It updates all configurations (not filtered) and returns the generic list (filtered).
export const ReloadMetadataTemplateConfigurationsButton = ({ filters }: { filters?: CustomImportFilters }) => {
  const { canReadIndex } = useEntityPermissions({ entityTypeId: "customImports" });
  const queryClient = useQueryClient();

  const { mutateAsync: handleReload } = usePostMutation<any, CustomImportFilters | undefined>({
    resource: `${customImportConstants.resource}/configurations/reload` as any,
  });

  return (
    <Button
      disabled={!canReadIndex}
      className="btn btn-default flex align-center gap-5"
      onClick={async () =>
        await handleReload({ body: { ...filters } })
          .then(() => {
            queryClient.invalidateQueries({ queryKey: [customImportConstants.resource] });
          })
          .catch((e) => console.error(e))
      }
    >
      <LucideIcon name="refresh-cw" />
      Reload configurations
    </Button>
  );
};
